import { useEffect, useState, useMemo, useRef } from "react";
import Sidebar from "../components/Sidebar";
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Stack,
    Chip,
    InputAdornment,
    Button,
    IconButton,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import GenericDataTable from "../components/GenericDataTable";
import SauvegardeModal from "../components/SauvegardeModal";
import EmailDetailModal from "../components/EmailDetailModal";
import ClientHistoryModal from "../components/ClientHistoryModal";
import {
    getMonitoredJobs,
    getClientsForFilter,
    createMonitoredJob,
    updateMonitoredJob,
    deleteMonitoredJob,
    syncAndAnalyzeEmails,
    markMonitoredJobCorrected,
} from "../Services/sauvegardesService";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useEmailDetailModal } from "../hooks/useEmailDetailModal";
import { formatDate } from "../utils/date";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const DAEMON_POLL_INTERVAL_MS = 30000;

function getLatestAnalysisTimestamp(jobs) {
    if (!Array.isArray(jobs) || jobs.length === 0) {
        return null;
    }

    let latestTimestamp = null;

    for (const job of jobs) {
        if (!job?.statusCheckedAt) {
            continue;
        }

        const ts = new Date(job.statusCheckedAt).getTime();
        if (Number.isNaN(ts)) {
            continue;
        }

        if (latestTimestamp === null || ts > latestTimestamp) {
            latestTimestamp = ts;
        }
    }

    return latestTimestamp;
}

function formatElapsedSince(milliseconds) {
    const safeMs = Number.isFinite(milliseconds)
        ? Math.max(0, milliseconds)
        : 0;
    const totalSeconds = Math.floor(safeMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
    }

    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
}

export default function SauvegardesPage() {
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncLoading, setSyncLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const { emailModalOpen, selectedJob: selectedJobForEmail, selectedReportData, handleViewEmail, closeEmailModal } = useEmailDetailModal();
    const [clientHistoryModalOpen, setClientHistoryModalOpen] = useState(false);
    const didAutoSync = useRef(false);

    // Filtres
    const [searchText, setSearchText] = useState("");
    const [filterClient, setFilterClient] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [clockNow, setClockNow] = useState(() => Date.now());

    const { showToast } = useToast();
    const showToastRef = useRef(showToast);

    useEffect(() => {
        showToastRef.current = showToast;
    }, [showToast]);

    const { user } = useAuth();
    const canCreateSauvegardes = Boolean(
        user?.hasPermission?.("sauvegardes", "create"),
    );
    const canEditSauvegardes = Boolean(
        user?.hasPermission?.("sauvegardes", "edit"),
    );
    const canDeleteSauvegardes = Boolean(
        user?.hasPermission?.("sauvegardes", "delete"),
    );

    // États possibles des sauvegardes
    const statusOptions = [
        { value: "OK", label: "OK", color: "success" },
        { value: "WARNING", label: "WARNING", color: "warning" },
        { value: "FAILED", label: "FAILED", color: "error" },
        { value: "CORRIGE", label: "CORRIGÉ", color: "info" },
        { value: "UNKNOWN", label: "UNKNOWN", color: "default" },
    ];

    // Chargement initial des données et des filtres
    useEffect(() => {
        let isMounted = true;

        async function loadData({ showLoader = true } = {}) {
            if (showLoader && isMounted) {
                setLoading(true);
            }

            try {
                const [jobsRes, clientsList] = await Promise.all([
                    getMonitoredJobs(),
                    getClientsForFilter(),
                ]);

                if (!isMounted) return;

                // Extraire les données correctement selon la structure de réponse
                const jobsData = jobsRes?.data || jobsRes || [];
                const jobsArray = Array.isArray(jobsData) ? jobsData : [];

                setJobs(jobsArray);
                setClients(Array.isArray(clientsList) ? clientsList : []);
            } catch (e) {
                console.error("Error loading data:", e);
                if (!isMounted) return;

                setJobs([]);
                showToastRef.current(
                    "Erreur lors du chargement des sauvegardes",
                    {
                        severity: "error",
                    },
                );
            } finally {
                if (showLoader && isMounted) {
                    setLoading(false);
                }
            }
        }

        async function autoSyncOnPageEnter() {
            if (didAutoSync.current) return;
            didAutoSync.current = true;

            try {
                if (isMounted) setSyncLoading(true);

                const result = await syncAndAnalyzeEmails({
                    daysBack: 7,
                    statusTypeName: "COMPLETED",
                    emailLimit: 100,
                });

                if (isMounted) {
                    const warnings = result?.warnings ?? [];
                    if (warnings.length > 0) {
                        warnings.forEach((w) =>
                            showToastRef.current(w, { severity: "warning" }),
                        );
                    }
                    showToastRef.current(
                        `Analyse automatique terminée (${result?.reports?.length || 0} rapport(s))`,
                        { severity: "success" },
                    );
                }
            } catch (e) {
                console.error("Auto sync error:", e);
                if (isMounted) {
                    showToastRef.current(
                        "Erreur lors de l'analyse automatique",
                        {
                            severity: "error",
                        },
                    );
                }
            } finally {
                if (isMounted) setSyncLoading(false);
            }
        }

        async function initializePage() {
            await loadData({ showLoader: true });
            await autoSyncOnPageEnter();
            await loadData({ showLoader: false });
        }

        initializePage();

        return () => {
            isMounted = false;
        };
    }, []);

    // Compteur temps réel pour "dernière analyse il y a".
    useEffect(() => {
        const intervalId = setInterval(() => {
            setClockNow(Date.now());
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    // Rafraîchit les jobs en fond pour suivre les itérations du daemon sans action manuelle.
    useEffect(() => {
        let isMounted = true;

        const refreshJobsSilently = async () => {
            try {
                const jobsRes = await getMonitoredJobs();
                if (!isMounted) return;

                const jobsData = jobsRes?.data || jobsRes || [];
                const jobsArray = Array.isArray(jobsData) ? jobsData : [];
                setJobs(jobsArray);
            } catch (e) {
                console.error("Background jobs refresh error:", e);
            }
        };

        const intervalId = setInterval(
            refreshJobsSilently,
            DAEMON_POLL_INTERVAL_MS,
        );

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, []);

    const latestAnalysisTimestamp = useMemo(
        () => getLatestAnalysisTimestamp(jobs),
        [jobs],
    );

    const latestAnalysisElapsedLabel = useMemo(() => {
        if (!latestAnalysisTimestamp) {
            return null;
        }

        return formatElapsedSince(clockNow - latestAnalysisTimestamp);
    }, [clockNow, latestAnalysisTimestamp]);

    // Construire un map clientId -> nom client
    const clientsMap = useMemo(() => {
        const map = {};
        clients.forEach((c) => {
            map[c.id] = c.nom || c.name || `Client #${c.id}`;
        });
        return map;
    }, [clients]);

    // Filtrage local des jobs
    const filteredJobs = useMemo(() => {
        const filtered = jobs.filter((job) => {
            // Filtre texte (nom du job)
            if (
                searchText &&
                !job.name?.toLowerCase().includes(searchText.toLowerCase())
            ) {
                return false;
            }
            // Filtre client
            if (filterClient && String(job.clientId) !== String(filterClient)) {
                return false;
            }
            // Filtre état
            if (filterStatus) {
                const jobStatus = job.status || "UNKNOWN";
                if (jobStatus !== filterStatus) {
                    return false;
                }
            }
            return true;
        });

        // Tri par gravité du statut (du plus grave au moins grave), puis par date en cas d'égalité
        const statusPriority = { FAILED: 1, WARNING: 2, UNKNOWN: 3, OK: 4 };
        return filtered.sort((a, b) => {
            const pA = statusPriority[a.status || "UNKNOWN"] ?? 99;
            const pB = statusPriority[b.status || "UNKNOWN"] ?? 99;
            if (pA !== pB) return pA - pB;
            // À gravité égale, plus récent d'abord
            return (
                new Date(b.editedAt || b.createdAt) -
                new Date(a.editedAt || a.createdAt)
            );
        });
    }, [jobs, searchText, filterClient, filterStatus]);

    // Handlers CRUD
    const handleAdd = () => {
        if (!canCreateSauvegardes) {
            showToast(
                "Vous n'avez pas les droits pour ajouter une sauvegarde",
                {
                    severity: "warning",
                },
            );
            return;
        }

        setEditData(null);
        setModalOpen(true);
    };

    const handleEdit = (job) => {
        if (!canEditSauvegardes) {
            showToast(
                "Vous n'avez pas les droits pour modifier une sauvegarde",
                {
                    severity: "warning",
                },
            );
            return;
        }

        setEditData(job);
        setModalOpen(true);
    };

    const handleDelete = (job) => {
        if (!canDeleteSauvegardes) {
            showToast(
                "Vous n'avez pas les droits pour supprimer une sauvegarde",
                {
                    severity: "warning",
                },
            );
            return;
        }

        if (window.confirm(`Supprimer la sauvegarde "${job.name}" ?`)) {
            deleteMonitoredJob(job.id)
                .then(() => {
                    setJobs(jobs.filter((j) => j.id !== job.id));
                    showToast("Sauvegarde supprimée", { severity: "success" });
                })
                .catch((e) => {
                    console.error("Delete error:", e);
                    showToast("Erreur lors de la suppression", {
                        severity: "error",
                    });
                });
        }
    };

    const handleSync = async () => {
        try {
            setSyncLoading(true);
            const result = await syncAndAnalyzeEmails({
                daysBack: 7,
                statusTypeName: "COMPLETED",
                emailLimit: 100,
            });

            const warnings = result?.warnings ?? [];
            if (warnings.length > 0) {
                warnings.forEach((w) => showToast(w, { severity: "warning" }));
            }

            showToast(
                `Sync complété: ${result.reports?.length || 0} rapport(s) généré(s)`,
                { severity: "success" },
            );

            // Recharger les jobs après le sync
            const jobsRes = await getMonitoredJobs();
            const jobsData = jobsRes?.data || jobsRes || [];
            const jobsArray = Array.isArray(jobsData) ? jobsData : [];
            setJobs(jobsArray);
        } catch (e) {
            console.error("Sync error:", e);
            showToast("Erreur lors de la synchronisation", {
                severity: "error",
            });
        } finally {
            setSyncLoading(false);
        }
    };

    const handleMarkCorrected = async (job) => {
        if (!canEditSauvegardes) {
            showToast(
                "Vous n'avez pas les droits pour modifier une sauvegarde",
                {
                    severity: "warning",
                },
            );
            return;
        }

        const status = job.status || "UNKNOWN";
        if (!["WARNING", "FAILED"].includes(status)) {
            showToast(
                "Seuls les jobs en WARNING ou FAILED peuvent être marqués comme corrigés",
                { severity: "info" },
            );
            return;
        }

        if (
            !window.confirm(
                `Marquer la sauvegarde "${job.name}" comme corrigée ?\n\n` +
                    "Cela créera un nouveau statut 'CORRIGÉ' avec votre nom et la date.",
            )
        ) {
            return;
        }

        try {
            await markMonitoredJobCorrected(job.id);

            const jobsRes = await getMonitoredJobs();
            const jobsData = jobsRes?.data || jobsRes || [];
            const jobsArray = Array.isArray(jobsData) ? jobsData : [];
            setJobs(jobsArray);

            showToast("Statut de la sauvegarde marqué comme corrigé", {
                severity: "success",
            });
        } catch (e) {
            console.error("Mark corrected error:", e);
            const errorMsg =
                e.response?.data?.message ||
                "Erreur lors du changement de statut en corrigé";
            showToast(errorMsg, { severity: "error" });
        }
    };

    const handleSave = async (formData) => {
        try {
            // Préparer les données de la sauvegarde
            const jobData = {
                name: formData.name,
                clientId: formData.clientId,
                frequencyType: formData.frequencyType,
                expectedDays: formData.expectedDays ?? [],
                expectedEmailCount: formData.expectedEmailCount,
                windowStart: formData.windowStart,
                windowEnd: formData.windowEnd,
                expectedSenders: formData.expectedSenders ?? [],
                subjectPattern: formData.subjectPattern,
                successKeywords: formData.successKeywords ?? [],
                failureKeywords: formData.failureKeywords ?? [],
                alertKeywords: formData.alertKeywords ?? [],
            };

            if (editData) {
                const updated = await updateMonitoredJob(editData.id, jobData);
                setJobs(jobs.map((j) => (j.id === updated.id ? updated : j)));
                setModalOpen(false);
                showToast("Sauvegarde mise à jour", { severity: "success" });
            } else {
                const created = await createMonitoredJob(jobData);
                setJobs([...jobs, created]);
                setModalOpen(false);
                showToast("Sauvegarde créée", { severity: "success" });
            }
        } catch (e) {
            console.error("Save error:", e);
            const errorMsg =
                e.response?.data?.message ||
                (editData
                    ? "Erreur lors de la mise à jour"
                    : "Erreur lors de la création");
            showToast(errorMsg, { severity: "error" });
        }
    };

    // Colonnes du tableau
    const columns = [
        { field: "name", headerName: "Job de sauvegarde" },
        {
            field: "clientName",
            headerName: "Client",
            renderCell: (row) =>
                clientsMap[row.clientId] || `Client #${row.clientId}`,
        },
        {
            field: "status",
            headerName: "État",
            renderCell: (row) => {
                const status = row.status || "UNKNOWN";
                const statusConfig = statusOptions.find(
                    (s) => s.value === status,
                ) || { label: status, color: "default" };

                const needsTooltip = ["WARNING", "FAILED", "UNKNOWN"].includes(
                    status,
                );

                const causeLines = {
                    WARNING: [
                        "⚠️ Emails d'alerte détectés (mots-clés d'alerte trouvés)",
                        "⚠️ Nombre d'emails reçus inférieur au nombre attendu",
                        "⚠️ Nombre d'emails reçus supérieur au nombre attendu",
                        "⚠️ Emails partiellement correspondants",
                    ],
                    FAILED: [
                        "❌ Emails d'échec détectés (mots-clés d'échec trouvés)",
                        "❌ La sauvegarde a explicitement signalé une erreur",
                    ],
                    UNKNOWN: [
                        "❓ Aucune analyse n'a encore été effectuée pour ce job",
                        "❓ Aucun email reçu sur la période analysée",
                    ],
                };

                const tooltipContent = needsTooltip ? (
                    <Box sx={{ p: 0.5, maxWidth: 320 }}>
                        {row.statusSummary && (
                            <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 600 }}
                            >
                                {row.statusSummary}
                            </Typography>
                        )}
                        {status !== "CORRIGE" && (
                            <>
                                <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{ mb: 0.5, opacity: 0.8 }}
                                >
                                    Causes possibles :
                                </Typography>
                                {(causeLines[status] || []).map((line, i) => (
                                    <Typography
                                        key={i}
                                        variant="caption"
                                        display="block"
                                    >
                                        {line}
                                    </Typography>
                                ))}
                            </>
                        )}
                    </Box>
                ) : (
                    ""
                );

                const chip = (
                    <Chip
                        label={statusConfig.label}
                        size="small"
                        color={statusConfig.color}
                    />
                );

                return needsTooltip ? (
                    <Tooltip
                        title={tooltipContent}
                        arrow
                        placement="top"
                        componentsProps={{
                            tooltip: {
                                sx: { bgcolor: "grey.900", maxWidth: 340 },
                            },
                            arrow: { sx: { color: "grey.900" } },
                        }}
                    >
                        <span>{chip}</span>
                    </Tooltip>
                ) : (
                    chip
                );
            },
        },
        {
            field: "expectedEmailCount",
            headerName: "Emails analysés / attendus",
            renderCell: (row) => {
                const received = Number.isFinite(row.receivedEmailCount)
                    ? row.receivedEmailCount
                    : 0;
                const expected = row.expectedEmailCount || 1;
                return (
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <Typography variant="body2">{received}</Typography>
                        <Typography variant="caption" color="text.secondary">/</Typography>
                        <Typography variant="body2">{expected}</Typography>
                    </Stack>
                );
            },
        },
        {
            field: "window",
            headerName: "Fenêtre attendue",
            renderCell: (row) => {
                const start = row.windowStart
                    ? row.windowStart.substring(0, 5)
                    : "—";
                const end = row.windowEnd ? row.windowEnd.substring(0, 5) : "—";
                return `${start} - ${end}`;
            },
        },
        {
            field: "createdAt",
            headerName: "Dernière mise à jour",
            renderCell: (row) =>
                row.statusLastEmailReceivedAt
                    ? formatDate(row.statusLastEmailReceivedAt)
                    : "—",
        },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (row) => (
                <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ width: "100%" }}
                >
                    <IconButton
                        size="small"
                        onClick={() => handleViewEmail(row)}
                        color="info"
                        title="Voir le dernier email"
                    >
                        <VisibilityIcon />
                    </IconButton>
                    {canEditSauvegardes &&
                        ["WARNING", "FAILED"].includes(row.status || "UNKNOWN") && (
                            <Tooltip title="Marquer comme corrigé">
                                <IconButton
                                    size="small"
                                    onClick={() => handleMarkCorrected(row)}
                                    color="success"
                                >
                                    <CheckCircleIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    {canEditSauvegardes && (
                        <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                            color="primary"
                            title="Modifier"
                        >
                            <EditIcon />
                        </IconButton>
                    )}
                    {canDeleteSauvegardes && (
                        <IconButton
                            size="small"
                            onClick={() => handleDelete(row)}
                            color="error"
                            title="Supprimer"
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Stack>
            ),
        },
    ];

    return (
        <div style={{ display: "flex", minHeight: "100vh" }}>
            <Sidebar />
            <Box
                sx={{
                    flex: 1,
                    p: { xs: 2, md: 3 },
                    width: "100%",
                    overflowX: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        Gestion des sauvegardes
                    </Typography>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems="center"
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleSync}
                            disabled={syncLoading}
                            fullWidth={{ xs: true, sm: false }}
                            startIcon={
                                syncLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <RefreshIcon />
                                )
                            }
                        >
                            {syncLoading
                                ? "Synchronisation..."
                                : "Synchroniser"}
                        </Button>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: { xs: "100%", sm: 260 } }}
                        >
                            Dernière analyse il y a :{" "}
                            {latestAnalysisElapsedLabel ?? "—"}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setClientHistoryModalOpen(true)}
                            startIcon={<HistoryIcon />}
                            fullWidth={{ xs: true, sm: false }}
                        >
                            Historique
                        </Button>
                        {canCreateSauvegardes && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAdd}
                                fullWidth={{ xs: true, sm: false }}
                            >
                                + Ajouter une sauvegarde
                            </Button>
                        )}
                    </Stack>
                </Stack>

                {/* Barre de filtres */}
                <Box sx={{ mb: 3 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <TextField
                            size="small"
                            placeholder="Rechercher une sauvegarde..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={{
                                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                minWidth: { sm: 220 },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            select
                            size="small"
                            label="Client"
                            value={filterClient}
                            onChange={(e) => setFilterClient(e.target.value)}
                            sx={{
                                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                minWidth: { sm: 180 },
                            }}
                        >
                            <MenuItem value="">Tous les clients</MenuItem>
                            {clients.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                    {c.nom || c.name || `Client #${c.id}`}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="État"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            sx={{
                                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                minWidth: { sm: 160 },
                            }}
                        >
                            <MenuItem value="">Tous les états</MenuItem>
                            {statusOptions.map((s) => (
                                <MenuItem key={s.value} value={s.value}>
                                    {s.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </Box>

                {loading ? (
                    <Typography color="text.secondary">
                        Chargement...
                    </Typography>
                ) : filteredJobs.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                        Aucune sauvegarde trouvée.
                    </Typography>
                ) : (
                    <GenericDataTable
                        columns={columns}
                        rows={filteredJobs}
                        bordered
                        headerAlign="center"
                        cellAlign="center"
                        headerSx={{
                            fontSize: 14,
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                        }}
                        cellSx={{ fontSize: 13 }}
                    />
                )}

                <SauvegardeModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onSave={handleSave}
                    initialData={editData}
                    clients={clients}
                />

                <EmailDetailModal
                    open={emailModalOpen}
                    onClose={closeEmailModal}
                    jobId={selectedJobForEmail?.id}
                    jobName={selectedJobForEmail?.name}
                    reportData={selectedReportData}
                />

                <ClientHistoryModal
                    open={clientHistoryModalOpen}
                    onClose={() => setClientHistoryModalOpen(false)}
                    clients={clients}
                    jobs={jobs}
                    clientsMap={clientsMap}
                    onViewEmail={handleViewEmail}
                />
            </Box>
        </div>
    );
}
